import React from "react";
import {
  Box,
  // Button,
  Container,
  Heading,
  Stack,
  Text,
  VStack,
  useColorModeValue,
  Badge,
  Icon,
  HStack,
} from "@chakra-ui/react";
import { motion } from "framer-motion";
// import { IoMdArrowForward } from "react-icons/io";

import ReplayableGif from "../../../Common/ReplayableGif";
// import { handleCTA } from "../../../../utils";

// Motion components
const MotionBox = motion(Box);
const MotionHeading = motion(Heading);
const MotionText = motion(Text);
// const MotionButton = motion(Button);

// Animations
const fadeInUp = {
  initial: { opacity: 0, y: 30 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6, ease: "easeOut" },
};

interface Feature {
  title: string;
  description: string;
  icon: React.ReactNode | React.ComponentType;
  image?: string;
  color?: string;
  bgColor?: string;
  trustIndicators?: Array<{
    icon: React.ComponentType;
    label: string;
    value?: string;
    color: string;
  }>;
}

interface FeatureSectionsProps {
  features?: Feature[];
  bg?: string;
  py?: number;
  spacing?: number;
  buttonText?: string;
  buttonAction?: () => void;
  showFeatureNumbers?: boolean;
  featureNumberPrefix?: string;
}

function FeatureSections({
  features = [],
  bg,
  py = 0,
  spacing = 16,
  // buttonText = "Learn more",
  // buttonAction,
  showFeatureNumbers = true,
  featureNumberPrefix = "Feature",
}: FeatureSectionsProps) {
  // Medical-first color palette
  const sectionBg = useColorModeValue("gray.50", "gray.900");
  const backgroundColor = bg || sectionBg;
  const cardBg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const textPrimary = useColorModeValue("gray.900", "white");
  const textSecondary = useColorModeValue("gray.600", "gray.300");
  const purplePrimary = "#6366F1";

  // const handleButtonClick = () => {
  //   if (buttonAction) {
  //     buttonAction();
  //   } else {
  //     handleCTA("login");
  //   }
  // };

  return (
    <Box
      as="section"
      py={py}
      bg={backgroundColor}
      width="100%"
      height="100%"
      display="flex"
      alignItems="center"
    >
      <Container maxW="container.xl" height="100%">
        <VStack spacing={spacing} height="100%" justify="center">
          {features.map((feature, idx) => (
            <MotionBox
              key={idx}
              w="full"
              initial="initial"
              whileInView="animate"
              viewport={{ once: true, margin: "-100px" }}
              height="100%"
              display="flex"
              alignItems="center"
            >
              <Stack
                direction={{
                  base: "column",
                  lg: idx % 2 === 0 ? "row" : "row-reverse",
                }}
                spacing={{ base: 8, lg: 16 }}
                align="center"
              >
                {/* Content Section */}
                <MotionBox
                  flex="1"
                  textAlign={{ base: "center", lg: "left" }}
                  variants={fadeInUp}
                >
                  <VStack
                    spacing={6}
                    align={{ base: "center", lg: "flex-start" }}
                  >
                    {/* Feature Badge */}
                    {showFeatureNumbers && (
                      <Badge
                        px={3}
                        py={1}
                        bg={`${purplePrimary}15`}
                        color={purplePrimary}
                        fontSize="xs"
                        fontWeight="600"
                        borderRadius="full"
                        letterSpacing="wide"
                        textTransform="uppercase"
                        border={`1px solid ${purplePrimary}30`}
                      >
                        {`${featureNumberPrefix} ${idx + 1}`}
                      </Badge>
                    )}

                    {/* Feature Icon */}
                    <Box
                      p={3}
                      borderRadius="xl"
                      bg={feature.bgColor || `${purplePrimary}15`}
                      color={feature.color || purplePrimary}
                      width="fit-content"
                      boxShadow="0 4px 16px rgba(0, 0, 0, 0.08)"
                    >
                      {React.isValidElement(feature.icon) ? (
                        <Box
                          boxSize={8}
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                        >
                          {feature.icon}
                        </Box>
                      ) : (
                        <Icon
                          as={feature.icon as React.ComponentType}
                          boxSize={8}
                        />
                      )}
                    </Box>

                    {/* Title */}
                    <MotionHeading
                      size="xl"
                      lineHeight="1.2"
                      fontWeight="700"
                      color={textPrimary}
                      fontSize={{ base: "2xl", md: "3xl", lg: "4xl" }}
                      {...fadeInUp}
                    >
                      {feature.title}
                    </MotionHeading>

                    {/* Description */}
                    <MotionText
                      fontSize={{ base: "md", md: "lg" }}
                      color={textSecondary}
                      lineHeight="1.6"
                      maxW="600px"
                      {...fadeInUp}
                      transition={{ delay: 0.2 }}
                    >
                      {feature.description}
                    </MotionText>

                    {/* CTA Button */}
                    {/* <MotionButton
                      variant="outline"
                      size="md"
                      rightIcon={<IoMdArrowForward />}
                      borderRadius="xl"
                      borderWidth="2px"
                      borderColor={purplePrimary}
                      color={purplePrimary}
                      bg="transparent"
                      _hover={{
                        bg: purplePrimary,
                        color: "white",
                        transform: "translateY(-2px)",
                        boxShadow: "0 8px 24px rgba(99, 102, 241, 0.3)",
                      }}
                      _active={{
                        transform: "translateY(0)",
                      }}
                      onClick={handleButtonClick}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{
                        duration: 0.6,
                        ease: "easeOut",
                        delay: 0.3,
                      }}
                    >
                      {buttonText}
                    </MotionButton> */}

                    {/* Dynamic Trust Indicators */}
                    {feature.trustIndicators &&
                      feature.trustIndicators.length > 0 && (
                        <HStack spacing={4} pt={2}>
                          {feature.trustIndicators.map(
                            (indicator, indicatorIdx) => (
                              <VStack key={indicatorIdx} spacing={1}>
                                <Icon
                                  as={indicator.icon}
                                  boxSize={4}
                                  color={indicator.color}
                                />
                                <Text
                                  fontSize="xs"
                                  fontWeight="600"
                                  color={textPrimary}
                                >
                                  {indicator.value && `${indicator.value} `}
                                  {indicator.label}
                                </Text>
                              </VStack>
                            )
                          )}
                        </HStack>
                      )}
                  </VStack>
                </MotionBox>

                {/* Image Section */}
                {feature.image && (
                  <MotionBox
                    flex="1"
                    position="relative"
                    variants={fadeInUp}
                    transition={{ delay: 0.2 }}
                  >
                    <Box
                      position="relative"
                      borderRadius="2xl"
                      overflow="hidden"
                      boxShadow="0 8px 32px rgba(0, 0, 0, 0.12)"
                      border={`1px solid ${borderColor}`}
                      bg={cardBg}
                      p={4}
                    >
                      <ReplayableGif
                        gifSrc={feature.image}
                        styles={{
                          borderRadius: "1rem",
                          width: "100%",
                          height: "auto",
                          display: "block",
                        }}
                      />
                    </Box>
                  </MotionBox>
                )}
              </Stack>
            </MotionBox>
          ))}
        </VStack>
      </Container>
    </Box>
  );
}

export default FeatureSections; 